import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from "recharts";
import {
  TrendingUp, TrendingDown, Minus, DollarSign, Award, BarChart3, Loader2, Users,
  Calendar, CheckCircle2, XCircle, AlertCircle, Download, CreditCard, CalendarRange,
} from "lucide-react";
import {
  startOfMonth, endOfMonth, subMonths, format, startOfWeek, endOfWeek, subWeeks,
  subDays, parseISO,
} from "date-fns";
import { ptBR } from "date-fns/locale";

type Period = "month" | "last_month" | "week" | "last_week" | "custom";

const PERIOD_LABELS: Record<Period, string> = {
  month: "Este Mês",
  last_month: "Mês Passado",
  week: "Esta Semana",
  last_week: "Semana Passada",
  custom: "Personalizado",
};

function getPeriodDates(period: Period, customStart?: Date, customEnd?: Date) {
  const now = new Date();
  switch (period) {
    case "month":
      return {
        startDate: startOfMonth(now),
        endDate: endOfMonth(now),
        prevStartDate: startOfMonth(subMonths(now, 1)),
        prevEndDate: endOfMonth(subMonths(now, 1)),
      };
    case "last_month": {
      const last = subMonths(now, 1);
      return {
        startDate: startOfMonth(last),
        endDate: endOfMonth(last),
        prevStartDate: startOfMonth(subMonths(now, 2)),
        prevEndDate: endOfMonth(subMonths(now, 2)),
      };
    }
    case "week":
      return {
        startDate: startOfWeek(now, { weekStartsOn: 1 }),
        endDate: endOfWeek(now, { weekStartsOn: 1 }),
        prevStartDate: startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }),
        prevEndDate: endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }),
      };
    case "last_week": {
      const lastW = subWeeks(now, 1);
      return {
        startDate: startOfWeek(lastW, { weekStartsOn: 1 }),
        endDate: endOfWeek(lastW, { weekStartsOn: 1 }),
        prevStartDate: startOfWeek(subWeeks(now, 2), { weekStartsOn: 1 }),
        prevEndDate: endOfWeek(subWeeks(now, 2), { weekStartsOn: 1 }),
      };
    }
    case "custom": {
      const start = customStart ?? startOfMonth(now);
      const end = customEnd ?? endOfMonth(now);
      const diffMs = end.getTime() - start.getTime();
      const diffDays = Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)));
      return {
        startDate: start,
        endDate: end,
        prevStartDate: subDays(start, diffDays),
        prevEndDate: subDays(start, 1),
      };
    }
  }
}

function fmtBRL(value: number) {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function GrowthBadge({ growth }: { growth: number | null }) {
  if (growth === null) return <Badge variant="outline" className="text-xs text-muted-foreground">Novo</Badge>;
  if (growth > 0) return (
    <Badge className="text-xs bg-green-100 text-green-700 border-green-200 gap-1">
      <TrendingUp className="w-3 h-3" />+{growth.toFixed(1)}%
    </Badge>
  );
  if (growth < 0) return (
    <Badge className="text-xs bg-red-100 text-red-700 border-red-200 gap-1">
      <TrendingDown className="w-3 h-3" />{growth.toFixed(1)}%
    </Badge>
  );
  return <Badge variant="outline" className="text-xs gap-1"><Minus className="w-3 h-3" />0%</Badge>;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  dinheiro: "Dinheiro",
  pix: "PIX",
  credito: "Crédito",
  debito: "Débito",
  voucher: "Voucher",
  outro: "Outro",
};

const PAYMENT_COLORS: Record<string, string> = {
  dinheiro: "#22c55e",
  pix: "#3b82f6",
  credito: "#a855f7",
  debito: "#f59e0b",
  voucher: "#ec4899",
  outro: "#94a3b8",
};

export default function AdminReports() {
  const [period, setPeriod] = useState<Period>("month");
  const [customStartStr, setCustomStartStr] = useState(() => format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [customEndStr, setCustomEndStr] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [appliedCustomStart, setAppliedCustomStart] = useState<Date | undefined>(undefined);
  const [appliedCustomEnd, setAppliedCustomEnd] = useState<Date | undefined>(undefined);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);

  const dates = useMemo(
    () => getPeriodDates(period, appliedCustomStart, appliedCustomEnd),
    [period, appliedCustomStart, appliedCustomEnd]
  );

  function applyCustomDates() {
    const start = parseISO(customStartStr);
    const end = parseISO(customEndStr);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return;
    if (end < start) return;
    // Ajusta o início para 00:00:00 e o fim para 23:59:59 para incluir o dia inteiro
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    setAppliedCustomStart(start);
    setAppliedCustomEnd(end);
    setPeriod("custom");
  }

  const { data, isLoading } = trpc.analytics.getByProfessional.useQuery(dates);
  const { data: checkoutData, isLoading: checkoutLoading } = trpc.analytics.getCheckoutSummary.useQuery({
    startDate: dates.startDate,
    endDate: dates.endDate,
  });
  const { data: topServices = [], isLoading: topServicesLoading } = (trpc as any).analytics.getTopServices.useQuery({
    startDate: dates.startDate,
    endDate: dates.endDate,
  });

  const professionals = data?.professionals ?? [];
  const totalRevenue = data?.totalRevenue ?? 0;
  const totalAppts = data?.totalAppts ?? 0;
  const completedCount = data?.completedCount ?? 0;
  const cancelledCount = data?.cancelledCount ?? 0;
  const noShowCount = data?.noShowCount ?? 0;
  const pendingCount = data?.pendingCount ?? 0;
  const confirmedCount = data?.confirmedCount ?? 0;

  const chartData = professionals
    .filter((p) => p.currentTotal > 0)
    .slice(0, 8)
    .map((p) => ({
      name: p.name.split(" ")[0],
      fullName: p.name,
      value: p.currentTotal,
      color: p.color ?? "#C9A96E",
    }));

  // Dados para o gráfico de status
  const statusData = [
    { name: "Concluídos", value: completedCount, color: "#22c55e" },
    { name: "Cancelados", value: cancelledCount, color: "#ef4444" },
    { name: "Confirmados", value: confirmedCount, color: "#3b82f6" },
    { name: "Pendentes", value: pendingCount, color: "#f59e0b" },
    { name: "Não compareceu", value: noShowCount, color: "#94a3b8" },
  ].filter((s) => s.value > 0);

  // Dados de pagamento por método
  const paymentData = (checkoutData?.totalByMethod ?? []).map((m: any) => ({
    name: PAYMENT_METHOD_LABELS[m.paymentMethod] ?? m.paymentMethod ?? "Outro",
    method: m.paymentMethod,
    total: Number(m.total ?? 0),
    count: Number(m.count ?? 0),
    color: PAYMENT_COLORS[m.paymentMethod] ?? "#94a3b8",
  }));
  const selectedPayment = paymentData.find((payment) => payment.method === selectedPaymentMethod);
  const selectedPaymentRows = (checkoutData?.payments ?? [])
    .flatMap((payment: any) => {
      const completedAt = payment.completedAt ?? payment.paidAt ?? payment.startTime;
      if (payment.splits?.length) {
        return payment.splits
          .filter((split: any) => split.paymentMethod === selectedPaymentMethod)
          .map((split: any) => ({ ...payment, amount: Number(split.amount ?? 0), completedAt }));
      }
      return payment.paymentMethod === selectedPaymentMethod
        ? [{ ...payment, amount: Number(payment.totalPrice ?? 0), completedAt }]
        : [];
    })
    .sort((a: any, b: any) => new Date(a.completedAt).getTime() - new Date(b.completedAt).getTime());

  const periodLabel =
    format(dates.startDate, "dd/MM", { locale: ptBR }) +
    " – " +
    format(dates.endDate, "dd/MM/yyyy", { locale: ptBR });

  const conversionRate = totalAppts > 0 ? ((completedCount / totalAppts) * 100).toFixed(1) : "0";
  const appointmentLossCount = cancelledCount + noShowCount;
  const appointmentLossRate = totalAppts > 0 ? (appointmentLossCount / totalAppts) * 100 : 0;
  const appointmentLossGuidance = appointmentLossRate >= 20
    ? "Atenção: revise confirmações, regras de cancelamento e a lista de espera para reduzir vagas perdidas."
    : appointmentLossRate >= 10
      ? "Em acompanhamento: observe os horários e serviços com maior volume de cancelamentos ou faltas."
      : "Fluxo saudável: mantenha a confirmação e o acompanhamento de clientes antes do horário.";

  // Exportar CSV
  function exportCSV() {
    const rows = [
      ["Resumo do período", "Valor"],
      ["Período", periodLabel],
      ["Receita total (R$)", totalRevenue.toFixed(2)],
      ["Agendamentos", String(totalAppts)],
      ["Taxa de conclusão (%)", conversionRate],
      [],
      ["Status dos agendamentos", "Quantidade"],
      ...statusData.map((status) => [status.name, String(status.value)]),
      [],
      ["Forma de pagamento", "Receita (R$)", "Recebimentos"],
      ...paymentData.map((payment) => [payment.name, payment.total.toFixed(2), String(payment.count)]),
      [],
      ["Profissional", "Receita (R$)", "Atendimentos", "Crescimento (%)"],
      ...professionals.map((p) => [
        p.name,
        p.currentTotal.toFixed(2),
        String(p.completedCount),
        p.growth !== null ? p.growth.toFixed(1) : "—",
      ]),
    ];
    const csv = rows.map((r) => r.map((value) => {
      const text = String(value ?? "");
      return /[;"\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    }).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio-${period}-${format(dates.startDate, "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-[Cormorant_Garamond,serif] font-semibold">Relatórios</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{periodLabel}</p>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5">
              {(["month", "last_month", "week", "last_week"] as Period[]).map((p) => (
                <Button
                  key={p}
                  size="sm"
                  variant={period === p ? "default" : "outline"}
                  className={`h-8 text-xs ${period === p ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}`}
                  onClick={() => setPeriod(p)}
                >
                  {PERIOD_LABELS[p]}
                </Button>
              ))}
              <Button
                size="sm"
                variant={period === "custom" ? "default" : "outline"}
                className={`h-8 text-xs gap-1.5 ${period === "custom" ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}`}
                onClick={() => setPeriod("custom")}
              >
                <CalendarRange className="w-3.5 h-3.5" />
                Personalizado
              </Button>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs gap-1.5"
              onClick={exportCSV}
              disabled={professionals.length === 0}
            >
              <Download className="w-3.5 h-3.5" />
              Exportar CSV
            </Button>
          </div>

          {/* Seletor de datas personalizadas */}
          {period === "custom" && (
            <div className="flex flex-wrap items-end gap-2 bg-muted/40 border border-border/40 rounded-xl px-3 py-2">
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Data início</Label>
                <Input
                  type="date"
                  value={customStartStr}
                  onChange={(e) => setCustomStartStr(e.target.value)}
                  className="h-8 text-xs w-36"
                  max={customEndStr}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Data fim</Label>
                <Input
                  type="date"
                  value={customEndStr}
                  onChange={(e) => setCustomEndStr(e.target.value)}
                  className="h-8 text-xs w-36"
                  min={customStartStr}
                  max={format(new Date(), "yyyy-MM-dd")}
                />
              </div>
              <Button
                size="sm"
                className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                onClick={applyCustomDates}
              >
                Aplicar
              </Button>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : (
        <>
          {/* KPI Cards — linha 1: financeiro */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="border-border/40">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                  </div>
                  <p className="text-xs text-muted-foreground">Receita Total</p>
                </div>
                <p className="text-xl font-bold">{fmtBRL(totalRevenue)}</p>
                <div className="mt-1">
                  <GrowthBadge growth={data?.revenueGrowth ?? null} />
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/40">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <p className="text-xs text-muted-foreground">Agendamentos</p>
                </div>
                <p className="text-xl font-bold">{totalAppts}</p>
                <div className="mt-1">
                  <GrowthBadge growth={data?.apptsGrowth ?? null} />
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/40">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  </div>
                  <p className="text-xs text-muted-foreground">Concluídos</p>
                </div>
                <p className="text-xl font-bold">{completedCount}</p>
                <p className="text-xs text-muted-foreground mt-1">Taxa: {conversionRate}%</p>
              </CardContent>
            </Card>

            <Card className="border-border/40">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                    <Award className="w-4 h-4 text-amber-600" />
                  </div>
                  <p className="text-xs text-muted-foreground">Top Profissional</p>
                </div>
                <p className="text-sm font-bold truncate">{professionals[0]?.name ?? "—"}</p>
                {professionals[0] && (
                  <p className="text-xs text-muted-foreground mt-1">{fmtBRL(professionals[0].currentTotal)}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* KPI Cards — linha 2: status detalhado */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="border-border/40 bg-red-50/30">
              <CardContent className="p-3 flex items-center gap-3">
                <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Cancelados</p>
                  <p className="text-lg font-bold text-red-600">{cancelledCount}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/40 bg-slate-50/30">
              <CardContent className="p-3 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-slate-400 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Não compareceu</p>
                  <p className="text-lg font-bold text-slate-600">{noShowCount}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/40 bg-blue-50/30">
              <CardContent className="p-3 flex items-center gap-3">
                <Users className="w-5 h-5 text-blue-500 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Profissionais ativos</p>
                  <p className="text-lg font-bold text-blue-600">{professionals.filter((p) => p.currentTotal > 0).length}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/40 bg-amber-50/30">
              <CardContent className="p-3 flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-amber-500 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Ticket médio</p>
                  <p className="text-lg font-bold text-amber-600">
                    {completedCount > 0 ? fmtBRL(totalRevenue / completedCount) : "—"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {totalAppts > 0 && <Card className={appointmentLossRate >= 20 ? "border-amber-300 bg-amber-50/60" : "border-border/40 bg-muted/20"}><CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"><div className="flex items-start gap-3"><span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${appointmentLossRate >= 20 ? "bg-amber-100 text-amber-700" : "bg-primary/10 text-primary"}`}><AlertCircle className="h-4 w-4" /></span><div><p className="font-medium">Perdas da agenda</p><p className="mt-0.5 text-sm text-muted-foreground">{appointmentLossCount} cancelamento(s) ou falta(s) no período · {appointmentLossRate.toFixed(1).replace(".", ",")}% dos agendamentos.</p></div></div><p className={`max-w-md text-sm ${appointmentLossRate >= 20 ? "text-amber-800" : "text-muted-foreground"}`}>{appointmentLossGuidance}</p></CardContent></Card>}

          {/* Gráficos lado a lado */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Receita por profissional */}
            {chartData.length > 0 && (
              <Card className="border-border/40">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-amber-600" />
                    Receita por Profissional
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${v}`} width={65} />
                      <Tooltip
                        formatter={(value: number, _: string, props: any) => [
                          fmtBRL(value),
                          props.payload?.fullName ?? "",
                        ]}
                      />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Status dos agendamentos */}
            {statusData.length > 0 && (
              <Card className="border-border/40">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    Status dos Agendamentos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number, name: string) => [value, name]} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: "11px" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Formas de pagamento */}
          {!checkoutLoading && paymentData.length > 0 && (
            <Card className="border-border/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-purple-600" />
                  Formas de Pagamento
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {paymentData.map((m: any) => (
                    <button
                      type="button"
                      key={m.method}
                      className="rounded-xl border border-border/40 p-3 text-center text-foreground transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      style={{ borderLeftColor: m.color, borderLeftWidth: 3 }}
                      onClick={() => setSelectedPaymentMethod(m.method)}
                      aria-label={`Ver clientes pagos com ${m.name}`}
                    >
                      <p className="text-xs text-muted-foreground mb-1">{m.name}</p>
                      <p className="text-base font-bold">{fmtBRL(m.total)}</p>
                      <p className="text-xs text-muted-foreground">{m.count} pag.</p>
                      <p className="mt-2 text-[11px] font-medium text-primary">Ver clientes</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Dialog open={Boolean(selectedPaymentMethod)} onOpenChange={(open) => !open && setSelectedPaymentMethod(null)}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-purple-600" />
                  Clientes pagos com {selectedPayment?.name ?? "esta forma"}
                </DialogTitle>
                <DialogDescription>
                  Atendimentos com status concluído no período {periodLabel}, ordenados pelo horário de finalização.
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-border/50">
                {selectedPaymentRows.length === 0 ? (
                  <p className="p-6 text-center text-sm text-muted-foreground">Nenhum atendimento concluído encontrado para esta forma de pagamento.</p>
                ) : (
                  <div className="divide-y divide-border/50">
                    {selectedPaymentRows.map((row: any, index: number) => (
                      <div key={`${row.id}-${index}`} className="grid grid-cols-[1fr_auto] gap-3 px-4 py-3 sm:grid-cols-[1fr_auto_auto] sm:items-center">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{row.clientName || row.clientPhone || "Cliente não informado"}</p>
                          <p className="text-xs text-muted-foreground">{row.serviceName || "Serviço"}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">{format(new Date(row.completedAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}</p>
                        <p className="text-right text-sm font-semibold">{fmtBRL(row.amount)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Ranking de serviços */}
          <Card className="border-border/40">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-600" />
                Serviços mais vendidos
              </CardTitle>
              {!topServicesLoading && topServices.length > 0 && (
                <span className="text-xs text-muted-foreground">{topServices.length} serviço(s)</span>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {topServicesLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>
              ) : topServices.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm">Nenhum serviço concluído no período selecionado.</div>
              ) : (
                <div className="divide-y divide-border/40">
                  {topServices.map((service: any, index: number) => {
                    const share = totalRevenue > 0 ? (Number(service.revenue) / totalRevenue) * 100 : 0;
                    return (
                      <div key={service.serviceId ?? service.name} className="flex items-center gap-3 px-4 py-3">
                        <span className="w-6 text-center text-sm font-semibold text-amber-700">{index + 1}º</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{service.name}</p>
                          <div className="mt-1.5 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.min(share, 100)}%` }} />
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-bold">{fmtBRL(Number(service.revenue))}</p>
                          <p className="text-xs text-muted-foreground">{service.completedCount} atend.</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ranking de profissionais */}
          <Card className="border-border/40">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold">Ranking de Profissionais</CardTitle>
              {professionals.length > 0 && (
                <span className="text-xs text-muted-foreground">{professionals.length} profissional(is)</span>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {professionals.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm">
                  Nenhum dado disponível para o período selecionado.
                </div>
              ) : (
                <div className="divide-y divide-border/40">
                  {professionals.map((prof, idx) => {
                    const share = totalRevenue > 0 ? (prof.currentTotal / totalRevenue) * 100 : 0;
                    return (
                      <div key={prof.professionalId} className="flex items-center gap-3 px-4 py-3">
                        {/* Rank */}
                        <div className="w-7 text-center shrink-0">
                          {idx === 0 ? <span className="text-lg">🥇</span>
                            : idx === 1 ? <span className="text-lg">🥈</span>
                            : idx === 2 ? <span className="text-lg">🥉</span>
                            : <span className="text-sm font-medium text-muted-foreground">{idx + 1}º</span>}
                        </div>

                        {/* Color dot + name */}
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: prof.color ?? "#C9A96E" }} />
                          <p className="text-sm font-medium truncate">{prof.name}</p>
                        </div>

                        {/* Progress bar */}
                        <div className="hidden sm:flex flex-1 items-center gap-2">
                          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${share}%`, background: prof.color ?? "#C9A96E" }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground w-10 text-right">{share.toFixed(0)}%</span>
                        </div>

                        {/* Stats */}
                        <div className="text-right shrink-0">
                          <p className="text-sm font-bold">{fmtBRL(prof.currentTotal)}</p>
                          <p className="text-xs text-muted-foreground">{prof.completedCount} atend.</p>
                        </div>

                        {/* Growth */}
                        <div className="shrink-0">
                          <GrowthBadge growth={prof.growth} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
